import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

// ═══════════════════════════════════════════════════════════════
// SHARED STYLES (Matching the new Login UI)
// ═══════════════════════════════════════════════════════════════
const css = `
  @import url('https://fonts.cdnfonts.com/css/satoshi');
  @import url('https://fonts.cdnfonts.com/css/eina-01');
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,700;0,9..144,900;1,9..144,400&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Great+Vibes&display=swap');

  :root {
    --bg:#F9F4F0;--card:#FFFFFF;
    --brand:#5A6993;--brand-light:#A1B0D8;--brand-wash:rgba(90,105,147,.06);
    --sage:#7B8F8A;--peach:#E6A184;--peach-wash:rgba(230,161,132,.08);
    --ink:#2D364D;--muted:#9C8474;--sand:#D6D2C4;--sand-light:#EBE7DF;--cloud:#F4F7F9;
    --font:'Satoshi','Eina 01','Plus Jakarta Sans',sans-serif;
    --font-display:'Fraunces',serif;
    --r-md:14px;--r-lg:24px;--r-full:9999px;
    --ease-spring:.4s cubic-bezier(.22,.68,0,1.1);
    --sh-lg:0 8px 32px rgba(45,54,77,.08);
    --sh-input:0 1px 4px rgba(45,54,77,.03);
  }
  .hh-dark {
    --bg:#151820;--card:#1e2230;
    --brand:#8ea4c8;--brand-light:#6b7ea8;--brand-wash:rgba(142,164,200,.1);
    --sage:#8faaa3;--peach:#e8b09a;
    --ink:#e0ddd8;--muted:#9a9088;--sand:#4a4640;--sand-light:#2e2c28;--cloud:#1c2030;
    --sh-lg:0 8px 32px rgba(0,0,0,.3);
    --sh-input:0 1px 4px rgba(0,0,0,.15);
  }
  .hh-root * { box-sizing:border-box; margin:0; padding:0; }
  .hh-root {
    font-family:var(--font); min-height:100vh; min-height:100dvh;
    background:var(--bg); color:var(--ink);
    display:flex; align-items:center; justify-content:center;
    padding:20px; position:relative;
  }
  .hh-root::before {
    content:''; position:fixed; inset:0; z-index:0; pointer-events:none;
    background:radial-gradient(circle at 20% 0%,var(--brand-wash) 0%,transparent 50%),
               radial-gradient(circle at 80% 100%,var(--peach-wash) 0%,transparent 50%);
  }

  /* Setup Card */
  .hh-setup-card {
    background:var(--card); width:100%; max-width:520px;
    border-radius:var(--r-lg); padding:40px;
    box-shadow:var(--sh-lg); border:1px solid var(--sand-light);
    position:relative; z-index:1;
    animation:cardIn .6s var(--ease-spring) both;
  }
  @keyframes cardIn { from{opacity:0;transform:translateY(20px) scale(.98)} to{opacity:1;transform:translateY(0) scale(1)} }

  /* Typography */
  .hh-title { font-family:var(--font-display); font-size:1.8rem; font-weight:700; color:var(--ink); letter-spacing:-.02em; margin-bottom:8px; line-height:1.2; }
  .hh-sub { font-size:.9rem; color:var(--muted); margin-bottom:28px; line-height:1.5; }

  /* Steps */
  .hh-steps { display:flex; gap:6px; margin-bottom:32px; }
  .hh-step { flex:1; height:4px; border-radius:var(--r-full); background:var(--sand-light); transition:background .3s ease; }
  .hh-step.on { background:var(--brand); }

  /* Forms */
  .hh-row { display:flex; gap:16px; margin-bottom:16px; }
  @media(max-width:480px) { .hh-row { flex-direction:column; gap:0; } }
  .hh-field { margin-bottom:16px; flex:1; }
  .hh-label { display:block; font-size:.7rem; font-weight:700; color:var(--muted); margin-bottom:6px; text-transform:uppercase; letter-spacing:.08em; }
  
  .hh-input, .hh-select {
    width:100%; padding:13px 16px; font-family:var(--font); font-size:.9rem; font-weight:500;
    background:var(--cloud); color:var(--ink); border:1.5px solid var(--sand-light);
    border-radius:12px; outline:none; box-shadow:var(--sh-input);
    transition:all .2s ease;
  }
  .hh-input::placeholder { color:var(--sand); font-weight:400; }
  .hh-input:focus, .hh-select:focus { border-color:var(--brand-light); background:var(--card); box-shadow:0 0 0 4px var(--brand-wash); }
  
  /* Range Slider */
  .hh-range { width:100%; margin:10px 0; accent-color:var(--brand); cursor:pointer; }
  .hh-range-labels { display:flex; justify-content:space-between; font-size:.75rem; color:var(--muted); font-weight:600; margin-top:4px; }

  /* Consent Checkbox */
  .hh-consent { display:flex; align-items:flex-start; gap:12px; padding:16px; background:var(--peach-wash); border:1px solid rgba(230,161,132,.3); border-radius:12px; margin-bottom:16px; animation:fadeDown .4s ease; }
  @keyframes fadeDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:translateY(0)} }
  .hh-consent input { margin-top:3px; width:18px; height:18px; accent-color:var(--peach); cursor:pointer; flex-shrink:0; }
  .hh-consent label { font-size:.82rem; color:var(--ink); line-height:1.4; cursor:pointer; font-weight:500; }

  /* Errors */
  .hh-err { font-size:.8rem; color:#dc2626; margin-top:6px; font-weight:600; display:flex; align-items:center; gap:6px; }

  /* Buttons */
  .hh-actions { display:flex; gap:12px; margin-top:32px; }
  .hh-btn { flex:1; padding:14px; background:var(--brand); color:#fff; border:none; border-radius:12px; font-family:var(--font); font-weight:700; font-size:.9rem; cursor:pointer; transition:all .2s ease; box-shadow:0 4px 12px rgba(90,105,147,.15); }
  .hh-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 16px rgba(90,105,147,.2); background:#4d5c84; }
  .hh-btn:active:not(:disabled) { transform:scale(.98); }
  .hh-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
  
  .hh-ghost { flex:1; padding:14px; background:transparent; color:var(--muted); border:1.5px solid var(--sand-light); border-radius:12px; font-family:var(--font); font-weight:700; font-size:.9rem; cursor:pointer; transition:all .2s ease; }
  .hh-ghost:hover { background:var(--cloud); color:var(--ink); border-color:var(--sand); }
`;

export default function ProfileSetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Data State
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", age: "", gender: "", parentConsent: false,
    stressLevel: "5", emotionalState: "",
    emergencyName: "", emergencyRelation: "", emergencyContact: ""
  });

  // Derived logic for age
  const ageNum = parseInt(formData.age, 10);
  const isTooYoung = ageNum > 0 && ageNum <= 15;
  const needsConsent = ageNum > 15 && ageNum < 18;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setError(""); // Clear errors when user types
  };

  const handleNext = () => {
    // Step 1 Validation
    if (step === 1) {
      if (!formData.firstName || !formData.lastName || !formData.age) {
        return setError("Please fill out your name and age.");
      }
      if (isTooYoung) {
        return setError("You must be at least 16 to use HealHub.");
      }
      if (needsConsent && !formData.parentConsent) {
        return setError("Parental consent is required to continue.");
      }
    }
    // Step 2 Validation
    if (step === 2) {
      if (!formData.emotionalState) {
        return setError("Please select how you've been feeling.");
      }
    }
    
    setError("");
    setStep(s => s + 1);
  };

  const handleFinish = async () => {
    // Step 3 Validation
    if (!formData.emergencyName || !formData.emergencyRelation || !formData.emergencyContact) {
      return setError("Please provide an emergency contact.");
    }

    setLoading(true);
    setError("");

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Authentication error. Please log in again.");

      // Save everything at once
      await setDoc(
        doc(db, "users", user.uid),
        {
          profile: formData,
          profileCompleted: true,
          completedAt: new Date().toISOString()
        },
        { merge: true }
      );

      navigate("/onboarding");
    } catch (err) {
      console.error(err);
      setError("Failed to save profile. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <style>{css}</style>
      <div className="hh-root hh-dark"> {/* Remove hh-dark class for light mode */}
        <div className="hh-setup-card">
          
          {/* Progress Steps */}
          <div className="hh-steps">
            <div className={`hh-step ${step >= 1 ? "on" : ""}`} />
            <div className={`hh-step ${step >= 2 ? "on" : ""}`} />
            <div className={`hh-step ${step >= 3 ? "on" : ""}`} />
          </div>

          {/* ─── STEP 1: BASICS ─── */}
          {step === 1 && (
            <div className="hh-fade-in">
              <h2 className="hh-title">Welcome to HealHub 🌱</h2>
              <p className="hh-sub">Let's get your space set up. What should we call you?</p>

              <div className="hh-row">
                <div className="hh-field">
                  <label className="hh-label">First Name *</label>
                  <input className="hh-input" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleChange} autoFocus />
                </div>
                <div className="hh-field">
                  <label className="hh-label">Last Name *</label>
                  <input className="hh-input" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleChange} />
                </div>
              </div>

              <div className="hh-row">
                <div className="hh-field">
                  <label className="hh-label">Age *</label>
                  <input className="hh-input" type="number" name="age" placeholder="Your age" value={formData.age} onChange={handleChange} />
                </div>
                <div className="hh-field">
                  <label className="hh-label">Gender (Optional)</label>
                  <input className="hh-input" name="gender" placeholder="How you identify" value={formData.gender} onChange={handleChange} />
                </div>
              </div>

              {/* Dynamic Consent Box */}
              {needsConsent && (
                <div className="hh-consent">
                  <input type="checkbox" id="consent" name="parentConsent" checked={formData.parentConsent} onChange={handleChange} />
                  <label htmlFor="consent">
                    Because you are under 18, we need to make sure it's okay for you to be here. <strong>I confirm I have my parent or guardian's consent to use HealHub.</strong>
                  </label>
                </div>
              )}

              {error && <div className="hh-err">⚠️ {error}</div>}
              
              <div className="hh-actions">
                <button className="hh-btn" onClick={handleNext}>Continue →</button>
              </div>
            </div>
          )}

          {/* ─── STEP 2: MENTAL BASELINE ─── */}
          {step === 2 && (
            <div className="hh-fade-in">
              <h2 className="hh-title">How are you feeling?</h2>
              <p className="hh-sub">This helps us customize your dashboard so it feels right for you today.</p>

              <div className="hh-field" style={{ marginBottom: 28 }}>
                <label className="hh-label">Stress Level This Week: {formData.stressLevel}/10</label>
                <input 
                  type="range" className="hh-range" name="stressLevel" 
                  min="1" max="10" value={formData.stressLevel} onChange={handleChange} 
                />
                <div className="hh-range-labels">
                  <span>Very Calm</span>
                  <span>Very Stressed</span>
                </div>
              </div>

              <div className="hh-field">
                <label className="hh-label">Emotional State *</label>
                <select className="hh-select" name="emotionalState" value={formData.emotionalState} onChange={handleChange}>
                  <option value="" disabled>Select how you've been feeling</option>
                  <option value="great">Doing great</option>
                  <option value="okay">Doing okay</option>
                  <option value="neutral">Just neutral</option>
                  <option value="low">Feeling a bit low</option>
                  <option value="struggling">Struggling a lot right now</option>
                </select>
              </div>

              {error && <div className="hh-err">⚠️ {error}</div>}

              <div className="hh-actions">
                <button className="hh-ghost" onClick={() => { setStep(1); setError(""); }}>← Back</button>
                <button className="hh-btn" onClick={handleNext}>Almost done →</button>
              </div>
            </div>
          )}

          {/* ─── STEP 3: EMERGENCY CONTACT ─── */}
          {step === 3 && (
            <div className="hh-fade-in">
              <h2 className="hh-title">Your Safety Net 🛟</h2>
              <p className="hh-sub">We hope you never need it, but your safety is our priority. Who should we contact in an emergency?</p>

              <div className="hh-field">
                <label className="hh-label">Contact Name *</label>
                <input className="hh-input" name="emergencyName" placeholder="e.g., Jane Doe" value={formData.emergencyName} onChange={handleChange} />
              </div>

              <div className="hh-row">
                <div className="hh-field">
                  <label className="hh-label">Relation *</label>
                  <input className="hh-input" name="emergencyRelation" placeholder="e.g., Mother, Partner" value={formData.emergencyRelation} onChange={handleChange} />
                </div>
                <div className="hh-field">
                  <label className="hh-label">Phone or Email *</label>
                  <input className="hh-input" name="emergencyContact" placeholder="Contact info" value={formData.emergencyContact} onChange={handleChange} />
                </div>
              </div>

              {error && <div className="hh-err">⚠️ {error}</div>}

              <div className="hh-actions">
                <button className="hh-ghost" onClick={() => { setStep(2); setError(""); }} disabled={loading}>← Back</button>
                <button className="hh-btn" onClick={handleFinish} disabled={loading}>
                  {loading ? "Saving..." : "Finish & Enter HealHub 🎉"}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}